import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import moment from "moment";

import {clearUser, getUserState} from "../context/UserContext";
import {SignInScreen} from "../screens/signin/SignInScreen";

export type RestApiResponse<T> = {
  data?: T;
  error?: AxiosError;
  isSuccess: boolean;
  httpCode?: number;
};

export class BaseClient {
  axiosInstance: AxiosInstance;

  async get<T = object>(path: string, params: object = {}): Promise<RestApiResponse<T>> {
    try {
      const result = await this.axiosInstance.get(path, params);
      return BaseClient.createSuccessPromise<T>(result.data);
    } catch (e) {
      return BaseClient.createFailurePromise<T>(e);
    }
  }

  async post<T = object>(path: string, data: object = {}, config?: AxiosRequestConfig): Promise<RestApiResponse<T>> {
    try {
      const result = await this.axiosInstance.post(path, data, config);
      return BaseClient.createSuccessPromise<T>(result.data);
    } catch (e) {
      return BaseClient.createFailurePromise<T>(e);
    }
  }

  async put<T = object>(path: string, data: object = {}): Promise<RestApiResponse<T>> {
    try {
      const result = await this.axiosInstance.put(path, data);
      return BaseClient.createSuccessPromise<T>(result.data);
    } catch (e) {
      return BaseClient.createFailurePromise<T>(e);
    }
  }

  async delete<T = object>(path: string, data: object = {}): Promise<RestApiResponse<T>> {
    try {
      const result = await this.axiosInstance.delete(path, data);
      return BaseClient.createSuccessPromise<T>(result.data);
    } catch (e) {
      return BaseClient.createFailurePromise<T>(e);
    }
  }

  async sendFormData<T = object>(config: any): Promise<RestApiResponse<T>> {
    try {
      const result = await this.axiosInstance(config);
      return BaseClient.createSuccessPromise<T>(result.data);
    } catch (e) {
      return BaseClient.createFailurePromise<T>(e);
    }
  }

  private static createSuccessPromise<T>(data: T): Promise<RestApiResponse<T>> {
    return Promise.resolve<RestApiResponse<T>>({
      data,
      isSuccess: true,
    });
  }

  private static createFailurePromise<T>(error: AxiosError): Promise<RestApiResponse<T>> {
    return Promise.resolve<RestApiResponse<T>>({
      error,
      isSuccess: false,
      httpCode: error.response ? error.response.status : null,
    });
  }

  static checkAuthStatus() {
    const authState = getUserState();

    if(Boolean(authState.access_token === null) || parseInt(authState.session_expires_at) <= moment().unix()) {
      clearUser();
      window.location.replace(SignInScreen.routeName);

      return;
    }
  }
}

export class ApiClient extends BaseClient {
  constructor(headers: any, baseURL = "https://api.iclickipay.cm/api/v2/spa/") {
    super();

    this.axiosInstance = axios.create({
      baseURL,
      headers: headers,
      maxRedirects: 0,
    });
    this.axiosInstance.interceptors.request.use(
      async (config: AxiosRequestConfig) => {
        return config;
      },
      (err: AxiosError) => {
        return Promise.reject(err);
      }
    );
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }
}

export class PublicApiClient extends BaseClient {
  constructor(headers: any, baseURL = "https://api.iclickipay.cm/api/v2/public/") {
    super();

    this.axiosInstance = axios.create({
      baseURL,
      headers: headers,
      maxRedirects: 0,
    });
    this.axiosInstance.interceptors.request.use(
      async (config: AxiosRequestConfig) => {
        return config;
      },
      (err: AxiosError) => {
        return Promise.reject(err);
      }
    );
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }
}
